import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ClinicAvatar } from 'shared/components/Avatars';
import Tooltip from 'shared/components/Tooltip';
import styled from 'styled-components/macro';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { DateStringObject } from '..';
import {
  useSubscribeToMassTextAlertStatusSubscription,
  NumberOfPetParentsReached,
  SmsNotificationStatusType,
  useGetEstimatedSendTimeForMassTextAlertLazyQuery,
} from 'shared/types/graphql';
import { formatPluralization, truncateString, convertObjectArrayToCSV } from 'shared/utils';
import {
  MassTextAleryActivityLogFragment,
  AppointmentDateRangeType,
  useGetDeliveryReportForMassTextAlertQuery,
  MassTextAlertStatus,
} from 'shared/types/graphql';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import isEqual from 'lodash-es/isEqual';
import MassTextAlertStatusTag from './MassTextAlertStatusTag';
import { Box, HStack, Text } from '@televet/kibble-ui';

interface ActivityLogItem {
  clinicId: string;
  logItem: MassTextAleryActivityLogFragment;
  formattedDateString: DateStringObject | undefined;
  formattedDateRange: string;
  refetchActivityLogData: () => void;
  sendTimeData?: Pick<NumberOfPetParentsReached, 'numberOfPetPerents' | 'timeEstimateInSeconds'> | null;
}

const MassTextAlertsActivityLogItem = ({
  clinicId,
  logItem,
  formattedDateString,
  formattedDateRange,
  refetchActivityLogData,
  sendTimeData,
}: ActivityLogItem): JSX.Element => {
  const [hasEstimatePassed, setHasEstimatePassed] = useState(false);

  const dateRangeTypes = {
    [AppointmentDateRangeType.Today]: 'Today',
    [AppointmentDateRangeType.Tomorrow]: 'Tomorrow',
    [AppointmentDateRangeType.ThisWeek]: 'This Week',
    [AppointmentDateRangeType.NextWeek]: 'Next Week',
    [AppointmentDateRangeType.ThisMonth]: 'This Month',
    [AppointmentDateRangeType.Custom]: 'In A Custom Date Range',
  };

  const { data: reportData, refetch: refetchReportData } = useGetDeliveryReportForMassTextAlertQuery({
    variables: {
      massTextAlertId: logItem.id,
    },
  });

  useSubscribeToMassTextAlertStatusSubscription({
    variables: {
      where: {
        node: {
          id: {
            equals: logItem.id,
          },
        },
      },
    },
    skip: !logItem.id,
    onData: async () => {
      setTimeout(() => {
        refetchReportData();
      }, (sendTime || 30) * 1000);

      refetchActivityLogData();
    },
  });

  const [getEstimatedSendTime, { data, loading: sendTimeDataLoading }] =
    useGetEstimatedSendTimeForMassTextAlertLazyQuery({
      fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
      nextFetchPolicy: GraphQLFetchPolicies.CacheOnly,
    });

  useEffect(() => {
    if (logItem.status === MassTextAlertStatus.Processing && !data && !sendTimeDataLoading) {
      getEstimatedSendTime({
        variables: {
          clinicId: clinicId,
          appointmentDateTimeTo: logItem.appointmentDateTimeTo,
          appointmentDateTimeFrom: logItem.appointmentDateTimeFrom,
          alertBody: logItem.alertBody,
        },
      });
    }
  }, [
    clinicId,
    data,
    getEstimatedSendTime,
    logItem.alertBody,
    logItem.appointmentDateTimeFrom,
    logItem.appointmentDateTimeTo,
    logItem.status,
    sendTimeData,
    sendTimeDataLoading,
  ]);

  const sendTime = useMemo(() => {
    let time = 0;
    if (sendTimeData) {
      time = sendTimeData.timeEstimateInSeconds || 30;
    }

    time = data?.estimateNumberOfPetParentsReached?.timeEstimateInSeconds || 30;

    // set min timeout to 30s to avoid prematurely setting "Unknown" statuses
    return time >= 30 ? time : 30;
  }, [data?.estimateNumberOfPetParentsReached?.timeEstimateInSeconds, sendTimeData]);

  // update status to unknown after estimate passes without status update
  useEffect(() => {
    if (logItem.status !== MassTextAlertStatus.Processing || hasEstimatePassed) return;

    if (!sendTimeData) {
      setHasEstimatePassed(true);
      return;
    }

    const estimateTimeout = setTimeout(() => {
      setHasEstimatePassed(true);
    }, sendTime * 1000);

    return (): void => clearTimeout(estimateTimeout);
  }, [hasEstimatePassed, logItem.createdAt, logItem.status, sendTime, sendTimeData, refetchReportData]);

  interface ReportDataItem {
    petParentFullName: string;
    phoneNumber: string;
    smsStatus: string | null;
    errorDetails: string | null;
    messageText: string | null;
    timeStamp: string | null;
  }

  const exportCSVFile = useCallback(
    (items): void => {
      const headers: ReportDataItem = {
        petParentFullName: 'Client Full Name',
        phoneNumber: 'Client Phone Number',
        smsStatus: 'SMS Status',
        errorDetails: 'Error Details',
        messageText: 'Message Text',
        timeStamp: 'Time Stamp',
      };
      const fileTitle = `${logItem.createdAt}-mass-text-alert-report`;

      const getStatus = (status: SmsNotificationStatusType): string => {
        switch (status) {
          case SmsNotificationStatusType.Error:
          case SmsNotificationStatusType.Delivered:
            return status;
          case SmsNotificationStatusType.Sent:
          default:
            return hasEstimatePassed ? 'Unknown' : 'Sending';
        }
      };

      const getError = (error: string | null, status: SmsNotificationStatusType): string => {
        if (!!error) {
          return error;
        } else if (
          status === SmsNotificationStatusType.Error ||
          (status !== SmsNotificationStatusType.Delivered && hasEstimatePassed)
        ) {
          return 'Status not reported by Carrier';
        } else {
          return '';
        }
      };

      const data = items.map((item: ReportDataItem) => {
        return {
          petParentFullName: item.petParentFullName,
          phoneNumber: item.phoneNumber,
          smsStatus: getStatus(item.smsStatus as SmsNotificationStatusType),
          errorDetails: getError(item.errorDetails, item.smsStatus as SmsNotificationStatusType),
          messageText: logItem.alertBody.replaceAll(/,/g, ''),
          timeStamp: `${formattedDateString?.date} at ${formattedDateString?.time}`,
        };
      });

      data.unshift(headers);

      const jsonObject = JSON.stringify(data);
      const csv = convertObjectArrayToCSV(jsonObject);
      const exportedFilenmae = fileTitle + '.csv' || 'export.csv';
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', exportedFilenmae);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },
    [logItem.createdAt, logItem.alertBody, hasEstimatePassed, formattedDateString],
  );

  const handleDownloadReportButtonClick = useCallback((): void => {
    if (reportData?.massTextAlertReport) {
      exportCSVFile(reportData.massTextAlertReport);
    }
  }, [reportData, exportCSVFile]);

  const getCountByStatus = useCallback(
    (status: SmsNotificationStatusType) => {
      if (reportData?.massTextAlertReport?.length) {
        return reportData?.massTextAlertReport?.filter((alert) => alert?.smsStatus === status).length || 0;
      } else if (status === SmsNotificationStatusType.Sent && sendTimeData?.numberOfPetPerents) {
        return sendTimeData.numberOfPetPerents;
      } else {
        return 0;
      }
    },
    [reportData?.massTextAlertReport, sendTimeData?.numberOfPetPerents],
  );

  const stillProcessing = useMemo(() => {
    return (
      (logItem.status === MassTextAlertStatus.Processing ||
        !reportData?.massTextAlertReport?.some((alert) => alert?.smsStatus !== SmsNotificationStatusType.Sent)) &&
      !hasEstimatePassed
    );
  }, [hasEstimatePassed, logItem.status, reportData?.massTextAlertReport]);

  return (
    <HStack spacing={2} pt={4} px={4} className="MassAlert__ActivityLog-Card" key={logItem.id} align="flex-start">
      <ClinicAvatar clinicMember={logItem.createdByUser} />
      <Box className="MassAlert__ActivityLog-MessageContent" fontSize="sm">
        <MessageTitle>
          <span>{logItem.createdByUser.firstName}</span> sent an alert to
          <Tooltip
            content={`${formatPluralization({
              word: 'Appointment',
              singularCriteria: sendTimeData?.numberOfPetPerents || 0,
            })} ${formattedDateRange}`}
          >
            <span> Appointments {dateRangeTypes[logItem.appointmentDateRangeType]}</span>
          </Tooltip>
        </MessageTitle>
        <Tooltip content={logItem.alertBody}>
          <MessageCopy>&quot;{truncateString(logItem.alertBody, 30)}&quot;</MessageCopy>
        </Tooltip>
        <MessageMetadata>
          <span>{formattedDateString?.time}</span>
          <span>{formattedDateString?.date}</span>
          <DownloadCSVButton
            disabled={
              (logItem.status === MassTextAlertStatus.Processing && !hasEstimatePassed) ||
              !reportData?.massTextAlertReport?.length
            }
            data-mixpanel-name="Download mass text alert report button"
            onClick={handleDownloadReportButtonClick}
          >
            Download Report
          </DownloadCSVButton>
        </MessageMetadata>
        {stillProcessing ? (
          <HStack className="MassAlert__ActivityLog-StatusIndicator">
            <Icon name="loader" size="xs" variant="info" style={{ animation: `rotation 2s infinite linear` }} />{' '}
            <Text size="xs" variant="info">
              Processing...
            </Text>
          </HStack>
        ) : (
          <>
            {!!getCountByStatus(SmsNotificationStatusType.Delivered) && (
              <MassTextAlertStatusTag
                status={SmsNotificationStatusType.Delivered}
                count={getCountByStatus(SmsNotificationStatusType.Delivered)}
                iconName="checkmarkBox"
                iconVariant="success"
              />
            )}
            {!!getCountByStatus(SmsNotificationStatusType.Error) && (
              <MassTextAlertStatusTag
                status={SmsNotificationStatusType.Error}
                count={getCountByStatus(SmsNotificationStatusType.Error)}
                iconName="alertCircle"
                iconVariant="danger"
              />
            )}
            {!!getCountByStatus(SmsNotificationStatusType.Sent) && (
              <MassTextAlertStatusTag
                status={SmsNotificationStatusType.Sent}
                count={getCountByStatus(SmsNotificationStatusType.Sent)}
                iconName="infoCircle"
                iconVariant="subtle"
              />
            )}
          </>
        )}
      </Box>
    </HStack>
  );
};

// don't rerender each log item every time the composer text or date range changes
function propCompare(prev: ActivityLogItem, next: ActivityLogItem): boolean {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { refetchActivityLogData: prevRefetch, sendTimeData: prevSendTimeData, ...prevToCompare } = prev;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { refetchActivityLogData: nextRefetch, sendTimeData: nextSendTimeData, ...nextToCompare } = next;
  const equal = isEqual(prevToCompare, nextToCompare);
  return equal;
}

export default React.memo(MassTextAlertsActivityLogItem, propCompare);

const MessageTitle = styled.div`
  span {
    font-weight: 600;
  }
`;

const MessageCopy = styled.div`
  cursor: pointer;
  margin-top: 3px;
  display: inline-block;
`;

const MessageMetadata = styled.div`
  display: flex;
  font-size: 12px;
  line-height: 21px;
  gap: 15px;
  margin-top: 3px;

  span:nth-child(-n + 2) {
    position: relative;

    &::after {
      content: '';
      display: block;
      position: absolute;
      width: 4px;
      height: 4px;
      border-radius: 5px;
      right: -12px;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
`;

const DownloadCSVButton = styled.button`
  text-decoration: underline;

  &:disabled {
    cursor: not-allowed;
    text-decoration: none;
  }
`;
